<template>
  <div class="layout">
    <aside class="sidebar-filters">
      <div>
        <label>FG:</label>
        <input v-model="fgFilter" placeholder="Filter FG" type="text" />
      </div>
      <div>
        <label>
          <input type="checkbox" v-model="showNegativeStockOnly" />
          Show Negative Stock Only
        </label>
      </div>
      <div>
        <label>Net Available:</label>
        <select v-model="netAvailableFilterType">
          <option value="lt">Less Than</option>
          <option value="gt">Greater Than</option>
        </select>
        <div v-if="netAvailableFilterType === 'between'" style="display: flex; gap: 0.5em;">
          <input v-model.number="netAvailableValue" placeholder="Min" type="number" style="width: 45%;" />
          <input v-model.number="netAvailableValue2" placeholder="Max" type="number" style="width: 45%;" />
        </div>
        <div v-else>
          <input v-model.number="netAvailableValue" placeholder="Value" type="number" style="width: 100%;" />
        </div>
      </div>
      <div>
        <label>Status:</label>
        <div class="checkbox-group">
          <label v-for="status in statusOptions" :key="status" class="checkbox-label">
            <input type="checkbox" :value="status" v-model="statusFilter" />
            {{ status }}
          </label>
        </div>
      </div>
      <div>
        <label>Movement Type:</label>
        <div class="checkbox-group">
          <label v-for="type in movementTypeOptions" :key="type" class="checkbox-label">
            <input type="checkbox" :value="type" v-model="movementTypeFilter" />
            {{ type }}
          </label>
        </div>
      </div>
      <button @click="restoreDefaults" class="restore-btn">Restore Default Filters</button>
    </aside>
    <div class="table-container">
      <table>
        <thead>
          <tr>
            <th>
              FG
              <button @click="toggleFgSort" class="sort-btn">
                <span v-if="fgSort === 'asc'">▲</span>
                <span v-else-if="fgSort === 'desc'">▼</span>
                <span v-else style="color:#bbb;">↕</span>
              </button>
            </th>
            <th>Outstanding</th>
            <th>Stock</th>
            <th>Net Available</th>
            <th>Reorder Level</th>
            <th>Pending Orders</th>
            <th>Status</th>
            <th>FG Required</th>
            <th>
              Order Prio
              <button @click="toggleOrderPrioSort" class="sort-btn">
                <span v-if="orderPrioSort === 'asc'">▲</span>
                <span v-else-if="orderPrioSort === 'desc'">▼</span>
                <span v-else style="color:#bbb;">↕</span>
              </button>
            </th>
            <th>No. of OAs</th>
            <th>Tag</th>
            <th>Movement Type</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in sortedRows" :key="idx" :class="{ 'stranger-row': row.L === 'Stranger' }">
            <td>{{ row.A }}</td>
            <td>{{ row.B }}</td>
            <td>{{ row.C }}</td>
            <td>{{ row.D }}</td>
            <td>{{ row.E }}</td>
            <td>{{ row.F }}</td>
            <td>{{ row.G }}</td>
            <td>{{ row.H }}</td>
            <td>{{ row.I }}</td>
            <td>{{ row.J }}</td>
            <td>{{ row.K }}</td>
            <td>{{ row.L }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';

const backendUrl = process.env.VUE_APP_BACKEND_URL;
const rowData = ref([]);

const fgFilter = ref('');
const netAvailableFilterType = ref('equals'); // default filter type
const netAvailableValue = ref('');
const netAvailableValue2 = ref(''); // for "between"
const statusFilter = ref(['To be assembled']);
const movementTypeFilter = ref([]);
const showNegativeStockOnly = ref(false);
const FILTERS_KEY = 'fgReqFilters';
const statusOptions = computed(() => {
  const set = new Set();
  rowData.value.forEach(row => { if (row.G) set.add(row.G); });
  return Array.from(set);
});
const movementTypeOptions = computed(() => {
  const set = new Set();
  rowData.value.forEach(row => { if (row.L) set.add(row.L); });
  return Array.from(set);
});
const filteredRows = computed(() =>
  rowData.value.filter(row => {
    const netAvailable = Number(row.D);
    const stock = Number(row.C);
    let netAvailablePass = true;
    if (netAvailableFilterType.value === 'lt' && netAvailableValue.value !== '') {
      netAvailablePass = netAvailable < netAvailableValue.value;
    } else if (netAvailableFilterType.value === 'gt' && netAvailableValue.value !== '') {
      netAvailablePass = netAvailable > netAvailableValue.value;
    }
    const stockPass = !showNegativeStockOnly.value || stock < 0;
    return (
      (!fgFilter.value || String(row.A).toLowerCase().includes(fgFilter.value.toLowerCase())) &&
      netAvailablePass &&
      (!statusFilter.value.length || statusFilter.value.includes(row.G)) &&
      (!movementTypeFilter.value.length || movementTypeFilter.value.includes(row.L)) &&
      stockPass
    );
  })
);
const fgSort = ref(null); // null, 'asc', or 'desc'
const orderPrioSort = ref(null); // null, 'asc', or 'desc'
const sortedRows = computed(() => {
  let rows = [...filteredRows.value];
  if (fgSort.value) {
    rows.sort((a, b) => {
      if (a.A === b.A) return 0;
      return fgSort.value === 'asc'
        ? String(a.A).localeCompare(String(b.A))
        : String(b.A).localeCompare(String(a.A));
    });
  } else if (orderPrioSort.value) {
    rows.sort((a, b) => {
      const aNum = Number(a.I);
      const bNum = Number(b.I);
      const aValid = !isNaN(aNum) && a.I !== '' && a.I !== null && a.I !== undefined;
      const bValid = !isNaN(bNum) && b.I !== '' && b.I !== null && b.I !== undefined;

      if (!aValid && !bValid) return 0;
      if (!aValid) return 1; // a is blank, goes last
      if (!bValid) return -1; // b is blank, goes last

      return orderPrioSort.value === 'asc'
        ? aNum - bNum
        : bNum - aNum;
    });
  }
  return rows;
});

// Fetch data
async function fetchData() {
  const response = await fetch(`${backendUrl}/excel/fg-req`);
  const data = await response.json();
  rowData.value = (data.items || []).map(row => ({
    A: row[0], B: row[1], C: row[2], D: row[3], E: row[4],
    F: row[5], G: row[6], H: row[7], I: row[8], J: row[9], K: row[10], L: row[11]
  }));
}
function restoreDefaults() {
  fgFilter.value = '';
  netAvailableFilterType.value = 'equals';
  netAvailableValue.value = '';
  netAvailableValue2.value = '';
  statusFilter.value = ['To be assembled'];
  movementTypeFilter.value = [];
  showNegativeStockOnly.value = false;
  localStorage.setItem(
    FILTERS_KEY,
    JSON.stringify({
      fgFilter: fgFilter.value,
      netAvailableFilterType: netAvailableFilterType.value,
      netAvailableValue: netAvailableValue.value,
      netAvailableValue2: netAvailableValue2.value,
      statusFilter: statusFilter.value,
      movementTypeFilter: movementTypeFilter.value,
      showNegativeStockOnly: showNegativeStockOnly.value,
    })
  );
}
function toggleFgSort() {
  fgSort.value = fgSort.value === 'asc' ? 'desc' : fgSort.value === 'desc' ? null : 'asc';
  orderPrioSort.value = null;
}
function toggleOrderPrioSort() {
  orderPrioSort.value = orderPrioSort.value === 'asc' ? 'desc' : orderPrioSort.value === 'desc' ? null : 'asc';
  fgSort.value = null;
}

onMounted(() => {
  const saved = localStorage.getItem(FILTERS_KEY);
  if (saved) {
    const filters = JSON.parse(saved);
    fgFilter.value = filters.fgFilter ?? '';
    netAvailableFilterType.value = filters.netAvailableFilterType ?? 'equals';
    netAvailableValue.value = filters.netAvailableValue ?? '';
    netAvailableValue2.value = filters.netAvailableValue2 ?? '';
    statusFilter.value = filters.statusFilter ?? ['To be assembled'];
    movementTypeFilter.value = filters.movementTypeFilter ?? [];
    showNegativeStockOnly.value = filters.showNegativeStockOnly ?? false;
    fgSort.value = filters.fgSort ?? null;
    orderPrioSort.value = filters.orderPrioSort ?? null;
  }
  fetchData();
});

watch(
  [
    fgFilter,
    netAvailableFilterType,
    netAvailableValue,
    netAvailableValue2,
    statusFilter,
    movementTypeFilter,
    showNegativeStockOnly,
    fgSort,
    orderPrioSort
  ],
  () => {
    localStorage.setItem(
      FILTERS_KEY,
      JSON.stringify({
        fgFilter: fgFilter.value,
        netAvailableFilterType: netAvailableFilterType.value,
        netAvailableValue: netAvailableValue.value,
        netAvailableValue2: netAvailableValue2.value,
        statusFilter: statusFilter.value,
        movementTypeFilter: movementTypeFilter.value,
        showNegativeStockOnly: showNegativeStockOnly.value,
        fgSort: fgSort.value,
        orderPrioSort: orderPrioSort.value
      })
    );
  },
  { deep: true }
);

</script>

<style>
.layout {
  display: flex;
  gap: 2em;
}
.sidebar-filters {
  min-width: 240px;
  background: #f9fafb;
  padding: 1.5em 1em;
  border-radius: 10px;
  box-shadow: 0 2px 12px #0001;
  display: flex;
  flex-direction: column;
  gap: 1.5em;
  font-family: 'Segoe UI', Arial, sans-serif;
}
.sidebar-filters > div {
  display: flex;
  flex-direction: column;
  gap: 0.5em;
}
.sidebar-filters > div > label {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.2em;
  letter-spacing: 0.02em;
}
.sidebar-filters input[type="text"],
.sidebar-filters input[type="number"] {
  padding: 0.5em 0.75em;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1em;
  background: #fff;
  transition: border 0.2s;
}
.sidebar-filters input[type="text"]:focus,
.sidebar-filters input[type="number"]:focus {
  border-color: #3182ce;
  outline: none;
}
.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.3em;
  padding-left: 0.5em;
}
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-weight: normal;
  color: #374151;
  cursor: pointer;
  font-size: 0.98em;
}
.checkbox-label input[type="checkbox"] {
  accent-color: #3182ce;
  width: 1.1em;
  height: 1.1em;
}
.table-container {
  flex: 1;
  overflow-x: auto;
}
table {
  border-collapse: collapse;
  width: 100%;
  font-size: 0.7rem;
}
th, td {
  border: 1px solid #ccc;
  padding: 0.5em 0.75em;
  text-align: left;
}
th {
  background: #f4f4f4;
}
.stranger-row {
  color: #d90429 !important;
  text-decoration: underline;
}
.restore-btn {
  margin-top: 1em;
  padding: 0.5em 1em;
  background: #3182ce;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: background 0.2s;
}
.restore-btn:hover {
  background: #225ea8;
}
.sort-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1em;
  margin-left: 0.3em;
  color: #3182ce;
  padding: 0;
  vertical-align: middle;
}
.sort-btn:focus {
  outline: none;
}
</style>